import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { GeneratorShell } from '../components/GeneratorShell';
import { PasswordStrengthBar } from '../components/PasswordStrengthBar';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import {
  buildCharset,
  estimateEntropyBits,
  generatePasswords,
} from '../lib/password';

const COUNT_PRESETS = [1, 5, 10, 25, 50, 100];

/**
 * `/tools/generate/password` — secure random password generator.
 *
 * The strength bar lives **above** the list rather than per-row
 * because every row in a single batch shares the same charset +
 * length, hence the same entropy. Per-row strength would be
 * misleading visual noise.
 */
export default function PasswordTool(): JSX.Element {
  const [length, setLength] = useState<number>(16);
  const [count, setCount] = useState<number>(5);
  const [lower, setLower] = useState(true);
  const [upper, setUpper] = useState(true);
  const [digits, setDigits] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(false);
  const [excludeNonTypeable, setExcludeNonTypeable] = useState(false);
  const [tick, setTick] = useState(0);

  const opts = {
    length,
    count,
    charsets: { lower, upper, digits, symbols },
    excludeAmbiguous,
    excludeNonTypeable,
  };

  const charsetSize = useMemo(() => buildCharset(opts).length, [
    lower,
    upper,
    digits,
    symbols,
    excludeAmbiguous,
    excludeNonTypeable,
  ]);
  const bits = estimateEntropyBits(length, charsetSize);

  const { items, error } = useMemo<{ items: string[]; error?: string }>(() => {
    try {
      return { items: generatePasswords(opts) };
    } catch (e) {
      return { items: [], error: e instanceof Error ? e.message : String(e) };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    length,
    count,
    lower,
    upper,
    digits,
    symbols,
    excludeAmbiguous,
    excludeNonTypeable,
    tick,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="password"
        category="generate"
        description="基于 crypto.getRandomValues 拒绝采样的随机密码；4 字符集可选 + 可滤除歧义/不易输入字符。"
      />

      <CryptoPrivacyBanner />

      <section className="card-pad flex flex-col gap-3">
        <LengthRow value={length} onChange={setLength} />
        <CharsetRow
          lower={lower}
          upper={upper}
          digits={digits}
          symbols={symbols}
          onLower={setLower}
          onUpper={setUpper}
          onDigits={setDigits}
          onSymbols={setSymbols}
        />
        <ExcludeRow
          excludeAmbiguous={excludeAmbiguous}
          excludeNonTypeable={excludeNonTypeable}
          onAmbiguous={setExcludeAmbiguous}
          onNonTypeable={setExcludeNonTypeable}
        />
        <PasswordStrengthBar bits={bits} />
      </section>

      <GeneratorShell
        controls={
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-ink-muted">数量</span>
            <select
              className="input py-0.5 text-[11.5px]"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
            >
              {COUNT_PRESETS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        }
        items={items}
        error={error}
        onRegenerate={() => setTick((t) => t + 1)}
      />
    </div>
  );
}

function LengthRow({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-16 text-ink-muted">长度</span>
      <input
        type="range"
        min={4}
        max={64}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 max-w-[280px]"
      />
      <input
        type="number"
        min={4}
        max={128}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        className="input w-20 py-1 text-xs"
      />
    </div>
  );
}

interface CharsetRowProps {
  lower: boolean;
  upper: boolean;
  digits: boolean;
  symbols: boolean;
  onLower: (v: boolean) => void;
  onUpper: (v: boolean) => void;
  onDigits: (v: boolean) => void;
  onSymbols: (v: boolean) => void;
}

function CharsetRow(p: CharsetRowProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="w-16 text-ink-muted">字符集</span>
      <Chip checked={p.lower} onChange={p.onLower} label="a-z" />
      <Chip checked={p.upper} onChange={p.onUpper} label="A-Z" />
      <Chip checked={p.digits} onChange={p.onDigits} label="0-9" />
      <Chip checked={p.symbols} onChange={p.onSymbols} label="!@#…" />
    </div>
  );
}

interface ExcludeRowProps {
  excludeAmbiguous: boolean;
  excludeNonTypeable: boolean;
  onAmbiguous: (v: boolean) => void;
  onNonTypeable: (v: boolean) => void;
}

function ExcludeRow(p: ExcludeRowProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="w-16 text-ink-muted">排除</span>
      <Chip
        checked={p.excludeAmbiguous}
        onChange={p.onAmbiguous}
        label="易混 0OoIl1"
      />
      <Chip
        checked={p.excludeNonTypeable}
        onChange={p.onNonTypeable}
        label={"不易输入 {}[]()/\"'`~,;:.<>\\|"}
      />
    </div>
  );
}

function Chip({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <label
      className={clsx(
        'inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-0.5 text-xs transition',
        checked
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-muted hover:border-accent/60',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      {label}
    </label>
  );
}
